import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, DoCheck, ElementRef,
  EventEmitter, Inject,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges, ViewChild
} from "@angular/core";
import * as _ from "lodash";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {fromEvent, Subscription, throttleTime} from "rxjs";
import {
  RoutesViewSettingsResourcesModel,
} from "../../../../routes/models/routes-view-settings.model";
import {RoutePanelModel} from "../../../../routes/models/route-panel.model";
import {CdkDragDrop, CdkDragEnter, moveItemInArray, transferArrayItem} from "@angular/cdk/drag-drop";
import {ResourcesComponentState, ResourcesComponentStore} from "../../state/resources.store";
import {ResourceAllocationTimeWindowExtModel} from "../../../models/resource-allocation-time-window-ext.model";
import {TaskModel} from "../../../../tasks/models/task.model";
import * as moment from "moment/moment";
import {TaskType} from "../../../../tasks/types/task.type";
import {RouteModel} from "../../../../routes/models/route.model";
import {SortType} from "../../../../commons/types/sort.type";
import {RoutesDragAndDropService} from "../../../../routes/angular/services/routes-drag-and-drop.service";
import {DEFAULT_RESOURCES_COLUMN_WIDTHS} from "../../../../routes/constants/routes-column-widths.constant";
import {RESOURCES_COLUMN_GROUPS} from "../../../../routes/constants/routes-column-groups.constant";

require('./resources-table-list.scss');
@Component({
  standalone: false,
  selector: 'app-resources-table-list',
  templateUrl: './resources-table-list.html',
  providers: [ResourcesComponentStore],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResourcesTableListComponent implements OnInit, OnChanges, OnDestroy {

  @Input() panel: RoutePanelModel;
  @Input() panels: RoutePanelModel[];
  @Input() routesDropListIds: string[];
  @Input() settings: RoutesViewSettingsResourcesModel;
  @Input() from?: string;
  @Input() to?: string;
  @Input() queryString?: string;
  @Input() bringozFilters?: any;
  @Input() selectedIds: string[];
  @Input() selectedServiceIds: string[];
  @Input() sortBy: any;
  @Input() sortType: SortType;
  @Input() itemElementSize: string;
  @Output() onAddServicesToResource = new EventEmitter<{resource: ResourceAllocationTimeWindowExtModel, serviceIds: string[]}>();
  @Output() onUpdateRouteResource = new EventEmitter<{routeId: string, resource: ResourceAllocationTimeWindowExtModel}>();
  @Output() onSelectServices = new EventEmitter<string[]>();
  @Output() onSelect = new EventEmitter<any>();
  @Output() fetchNextEvent = new EventEmitter<null>();
  @Output() onToggleVisible = new EventEmitter<boolean>();
  @Output() onClick = new EventEmitter<ResourceAllocationTimeWindowExtModel>();
  @Output() onDoubleClick = new EventEmitter<{resource: ResourceAllocationTimeWindowExtModel, event: any}>();
  @Output() onRemoveTaskFromRoute = new EventEmitter<any>(); // need to connect
  @Output() onResourcesDropListIdsChange = new EventEmitter<{panel: RoutePanelModel, ids: string[]}>();
  @Output() onSettingsChange = new EventEmitter<RoutesViewSettingsResourcesModel>();

  get uniqueId(): string {
    return 'resources-' + this.panel?.id;
  }

  public defaultWidths = DEFAULT_RESOURCES_COLUMN_WIDTHS;
  public columnGroups = RESOURCES_COLUMN_GROUPS;

  @ViewChild(CdkVirtualScrollViewport) private viewport: CdkVirtualScrollViewport;
  @ViewChild('headerContainer') private headerContainer: ElementRef;

  public vm: ResourcesComponentState;
  public selectedKeys: any;
  public visibleKeys: any;
  public allPanelIds: string[];

  private readonly loadLimit: number = 30;
  private vmSubscription: Subscription;
  private resizeScheduled: boolean;
  private clickTimeout: any | null = null;

  constructor(
    @Inject(ResourcesComponentStore) private resourcesStore: ResourcesComponentStore,

    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject(RoutesDragAndDropService) private routesDragAndDropService: RoutesDragAndDropService) {
  }

  ngOnInit() {
    this.vmSubscription = this.resourcesStore.vm$.subscribe((vm) => {
      const resourcesChanged = !this.vm || vm.resources !== this.vm.resources;

      if (resourcesChanged) {
        const ids = _.map(vm.resources, (r) => {
          return `${this.panel.id}-${r.id}`;
        });
        this.onResourcesDropListIdsChange.emit({panel: this.panel, ids: ids});
      }

      this.vm = {...vm};
      this.cdr.detectChanges();

      setTimeout(() => {
        if (this.viewport && !this.vm.loading && !!this.vm.cursor) {
          this.viewport.checkViewportSize();
          const nextBatchThreshold = this.viewport.getViewportSize();
          const bottomOffset = this.viewport.measureScrollOffset('bottom');

          if (bottomOffset < nextBatchThreshold) {
            this._getResources();
          }
        }
      }, 0);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.panels || changes.routesDropListIds) {
      const filteredPanels = _.filter(this.panels, (p) => {
        return p.id !== this.panel.id;
      })
      this.allPanelIds = _.concat(_.map(filteredPanels, 'id'), this.routesDropListIds || []);
    }
    if (changes.selectedIds) {
      this.selectedKeys = {};
      _.forEach(this.selectedIds, (id) => {
        this.selectedKeys[id] = true;
      });
    }
    if (
      changes.queryString  ||
      changes.from ||
      changes.to ||
      changes.sortBy ||
      changes.sortType ||
      changes.bringozFilters
    ) {
      this._resetStore();
      this._getResources();
    }
  }

  ngOnDestroy() {
    this.vmSubscription?.unsubscribe();
    this.resourcesStore?.unSubscribeAll();
  }

  onBodyScroll(event: Event) {
    if (this.headerContainer) {
      const target = event.target as HTMLElement;
      this.headerContainer.nativeElement.scrollLeft = target.scrollLeft;
    }
    this.getNextBatch(event);
  }

  getNextBatch(event: Event) {
    const nextBatchThreshold = this.viewport.getViewportSize();
    if (
      !this.vm.loading &&
      this.viewport.measureScrollOffset('bottom') < nextBatchThreshold &&
      !!this.vm.cursor
    ) {
      this._getResources();
    }
    this.viewport.checkViewportSize();
  }

  onToggleSelection(id, isSelected: boolean) {
    this.selectedKeys[id] = isSelected;
    const selectedIds = _.map(this.selectedKeys, (value, key) => {
      if (value) return key;
    });

    this.onSelect.emit(_.compact(selectedIds));
  }

  onRowClick(resource: ResourceAllocationTimeWindowExtModel, event: any) {
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }
    this.clickTimeout = setTimeout(() => {
      this.onClick.emit(resource);
      this.clickTimeout = null;
    }, 250);
  }

  onRowDoubleClick(resource: ResourceAllocationTimeWindowExtModel, event: any) {
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }
    event.stopPropagation();
    this.onDoubleClick.emit({resource: resource, event: event});
  }

  onDrop(resource: ResourceAllocationTimeWindowExtModel, event: CdkDragDrop<any[]>) {
    this.routesDragAndDropService.onDropResources(
      resource,
      event,
      this.selectedServiceIds
    ).then((result) => {
      if (result) {
        if (result.type === 'update') {
          this.onUpdateRouteResource.emit(result.data);
        } else if (result.type === 'add') {
          this.onAddServicesToResource.emit(result.data);
        }
      }
    });
  }

  private _resetStore() {
    this.resourcesStore.resetResources();
  }

  private _getResources() {
    this.resourcesStore.getResources(this._getquery());
  }

  private _getquery() {
    const query = {
      query: this.queryString,
      from: this.from,
      to: this.to,
      branchIds: this.bringozFilters.branchIds,
      agentIds: this.bringozFilters.agentIds,
      serviceAreaIds: this.bringozFilters.serviceAreaIds,
      tag: this.bringozFilters.tags,
      vehicleTypeIds: this.bringozFilters.vehicleTypeIds,
      hasRoutes: this.bringozFilters.inRoute,
      limit: this.loadLimit,
      sort: (this.sortBy && this.sortType) ? `${this.sortBy},${this.sortType}` : undefined
    }

    Object.keys(query).forEach(key => query[key] === undefined && delete query[key]);
    return query;
  }

  public trackById(_ :number, item: ResourceAllocationTimeWindowExtModel): string {
    return item.id;
  }

  public getTotalDuration(resource: ResourceAllocationTimeWindowExtModel): number {
    return resource.routes?.reduce((acc, route) => acc + route.totalTimeSec, 0) || 0;
  }

  public getTotalIncome(resource: ResourceAllocationTimeWindowExtModel): number {
    return resource.routes?.reduce((acc, route: any) => acc + (route.totals?.totalIncome || 0), 0) || 0;
  }

}
